import {CSSProperties, useContext, useState, useEffect} from 'react';
import ReactDOM from 'react-dom';
import cx from 'classnames';
import Skeleton from 'react-loading-skeleton';
import {Typography} from '~library/atoms/Typography';
import {useImageFilter} from '~/CreateFabricInvitation/hooks/useImageFilter';
import 'react-loading-skeleton/dist/skeleton.css';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {LoadingOverlay} from '~library/molecules/LoadingOverlay';
import './FilterPreview.sass';

const LOADING_MESSAGE = 'Applying image filter';

interface Canvas {
  getActiveObject: () => any;
  on: (action: string, cb: () => void) => void;
  off: (action: string, cb: () => void) => void;
}

interface FilterPreviewProps {
  photos: {
    id: string;
    src: string;
    label?: string;
    filterKey: string;
    cssFilter?: Record<string, string>;
  }[];
}

export const FilterPreview = ({photos}: FilterPreviewProps) => {
  const {canvas, processing} = useContext(EventCreateContext);

  const [activeObjectFilter, setActiveObjectFilter] = useState(() => {
    const activeObject = (canvas as unknown as Canvas)?.getActiveObject();
    if (!activeObject) return 'None';

    return activeObject?.filters?.[0]?.type || 'None';
  });

  const {applyImageFilter} = useImageFilter();

  function onPhotoClick(filterKey: string) {
    applyImageFilter(filterKey);
    setActiveObjectFilter(filterKey);
  }

  useEffect(() => {
    function handleObjectRestedUyo() {
      setActiveObjectFilter('None');
    }

    (canvas as unknown as Canvas)?.on('object:reseted:uyo', handleObjectRestedUyo);
    return () => {
      (canvas as unknown as Canvas)?.off('object:reseted:uyo', handleObjectRestedUyo);
    };
  }, [canvas]);

  return (
    <>
      <div className="filter-preview__wrapper">
        {photos.map(({id, src, label, filterKey, cssFilter}) => {
          const isFilterActive = activeObjectFilter.toLowerCase() === filterKey;
          return (
            <div
              key={id}
              className={cx('filter-preview__container', {
                'filter-preview__container--disable': processing,
              })}
              onClick={() => onPhotoClick(filterKey)}
              role="presentation"
            >
              <div
                id="filter_preview__img"
                className={cx('filter-preview__img', {
                  'filter-preview__img__wrapper--active': isFilterActive && src,
                  'filter-preview__img__wrapper--grid': isFilterActive && processing,
                })}
              >
                {src ? (
                  <>
                    {isFilterActive && processing ? (
                      <span className="evt-spinner inline no-margin filter-preview__spinner" />
                    ) : null}
                    <img
                      src={src}
                      alt="user-uploaded-image"
                      className={cx('filter-preview__img', {
                        'filter-preview__img--processing': isFilterActive && processing,
                        'filter-preview__img--processing--inactive': !isFilterActive && processing,
                      })}
                      style={cssFilter as CSSProperties}
                    />
                  </>
                ) : (
                  <Skeleton width={80} height={80} />
                )}
              </div>
              <Typography
                variant="label3"
                semibold={isFilterActive}
                classNames={cx('filter-preview__label', {
                  'filter-preview__label--active': isFilterActive,
                  'filter-preview__label--processing-inactive': !isFilterActive && processing,
                })}
              >
                {label}
              </Typography>
            </div>
          );
        })}
      </div>
      {processing &&
        ReactDOM.createPortal(
          <LoadingOverlay
            loadingMessage={LOADING_MESSAGE}
            show={processing}
            overlayClassName="filter-preview__loading-overlay"
          />,
          document.querySelector('.canvas-container')!
        )}
    </>
  );
};
